import _defineProperty from 'babel-runtime/helpers/defineProperty';
import _extends from 'babel-runtime/helpers/extends';

import { mapMutations, mapState, mapActions } from 'vuex';
import NormalGroupItem from './normal-group-item.vue';
import JsonGroupItem from './json-group-item.vue';
import I18nGroupItem from './i18n-group-item.vue';
import GlobalConfigGroupItem from './global-config-group-item.vue';
import SceneGroupItem from './scene-group-item.vue';
import ScreenGroupItem from './screen-group-item.vue';
import GroupLegend from '../components/group-legend.vue';
import ToggleVisibleBtn from './toggle-visible-button.vue';
import CategoryMirror from './category-mirror.vue';
import { is3DMaterialResource, isCocosModelAnimationResource, isModelResource } from '../utils';
import StarButton from '../components/star-button.vue';
import { isMultiGameDifficulty as _isMultiGameDifficulty } from '@/views/creative/utils';
import { categoryConfig, gameConfigCategoryResourceType, i18nCategoryResourceType, isPresetCategory as _isPresetCategory, presetConfigCategoryResourceType, isPresetOptionCategory, isPresetConfigParentCategory } from '@/store/creative/common/category';
import { groupItemMixin } from '@/views/creative/mixin/index.js';
import { isTextureResource } from '@/views/creative/utils';

export default {
  components: {
    NormalGroupItem: NormalGroupItem,
    JsonGroupItem: JsonGroupItem,
    I18nGroupItem: I18nGroupItem,
    GroupLegend: GroupLegend,
    ToggleVisibleBtn: ToggleVisibleBtn,
    CategoryMirror: CategoryMirror,
    StarButton: StarButton,
    GlobalConfigGroupItem: GlobalConfigGroupItem,
    SceneGroupItem: SceneGroupItem,
    ScreenGroupItem: ScreenGroupItem
  },

  name: 'GroupItem',

  props: {
    category: {
      type: Object,
      required: true
    },
    isSubCategory: {
      type: Boolean,
      default: false
    }
  },

  mixins: [groupItemMixin],

  data: function data() {
    return {
      fold: false
    };
  },


  computed: _extends({}, mapState({
    templateConfig: function templateConfig(state) {
      return state.creative.template.templateConfig;
    },
    hasPresetResource: function hasPresetResource(state) {
      return state.creative.template.hasPresetResource;
    },
    isCreativeTemplateCustom: function isCreativeTemplateCustom(state) {
      return state.creative.template.isCreativeTemplateCustom;
    },
    isTemplate: function isTemplate(state) {
      return state.creative.template.isTemplate;
    },
    isCommonTemplatePage: function isCommonTemplatePage(state) {
      return state.creative.template.isCommonTemplatePage;
    },
    isPreviewBasicPackage: function isPreviewBasicPackage(state) {
      return state.creative.template.isPreviewBasicPackage;
    }
  }), {
    classObject: function classObject() {
      var _ref;

      return _ref = {
        'group-item-normal': this.isNormal,
        'group-item-zip': this.isZip,
        'group-item-group': this.isGroup,
        'sub-group-item': this.isSubCategory,
        'group-item-json': this.isJson || this.isGameConfig,
        'group-item-i18n-packages': this.isI18nPackage,
        'group-item-image-language-package': this.isImageLanguagePackage,
        'group-item-playAgain': this.isPlayAgain,
        'group-item-autoEndTime': this.isAutoEndTime,
        'group-item-multiGameDifficulty': this.isMultiGameDifficulty
      }, _defineProperty(_ref, 'group-item-' + this.category.id, this.category.id), _defineProperty(_ref, 'group-item-3DModelConfig', this.is3DSceneConfig || this.is3DScreenConfig), _defineProperty(_ref, 'group-item-preset', this.isPresetConfigParent), _ref;
    },
    visible: function visible() {
      // 如果是预设坑位下，非镜头和预设 config 的坑位都隐藏
      if (this.category.pid === categoryConfig.preset.id && !(this.is3DScreenConfig || this.is3DSceneConfig || this.isPresetConfig || this.isPresetConfigParent)) {
        return false;
      }

      // 如果是预设选项，当 presetConfig 的值不一致时隐藏
      if (isPresetOptionCategory(this.category) && this.templateConfig.json_config.presetConfig[this.category.properties.preset].value !== this.category.prefix) {
        return false;
      }

      if (this.isPresetCategory) {
        return false;
      }

      // 组
      if (this.isGroup) {
        return Array.isArray(this.category.sub_category);
      }

      // 模型贴图-仅定制项目展示
      if (this.resourceList.length && isTextureResource(this.resourceList[0]) && !this.isCreativeTemplateCustom) {
        return false;
      }

      // 定制项目-模型资源不展示, 包含 pt 的以及创意方向的
      if (this.resourceList.length && (this.isCreativeTemplateCustom || this.isTemplate && !this.isCommonTemplatePage && !this.isPreviewBasicPackage) && isModelResource(this.resourceList[0])) {
        return false;
      }

      // 隐藏材质以及动作
      if (this.resourceList.length && (is3DMaterialResource(this.resourceList[0]) || isCocosModelAnimationResource(this.resourceList[0]))) {
        return false;
      }

      return true;
    },
    isJson: function isJson() {
      return this.category.resource_type === 'json';
    },
    isGameConfig: function isGameConfig() {
      return this.category.resource_type === gameConfigCategoryResourceType;
    },
    isI18nPackage: function isI18nPackage() {
      return this.isJson && this.category.prefix === 'i18n';
    },
    isI18n: function isI18n() {
      return this.category.resource_type === i18nCategoryResourceType;
    },
    isGlobalConfig: function isGlobalConfig() {
      return this.category.isGlobalConfig;
    },
    isImageLanguagePackage: function isImageLanguagePackage() {
      return this.category.resource_type === 'zip' && this.category.prefix === 'lang';
    },
    isGroup: function isGroup() {
      return this.category.resource_type === 'group' || isPresetOptionCategory(this.category) || isPresetConfigParentCategory(this.category);
    },
    isNormal: function isNormal() {
      return this.category.resource_type === 'normal';
    },
    isZip: function isZip() {
      return this.category.resource_type === 'zip';
    },
    is3DScreenConfig: function is3DScreenConfig() {
      return this.category.resource_type === '3DScreenConfig';
    },
    is3DSceneConfig: function is3DSceneConfig() {
      return this.category.resource_type === '3DSceneConfig';
    },
    isPlayAgain: function isPlayAgain() {
      return this.category.prefix === categoryConfig.playAgain.prefix;
    },
    isAutoEndTime: function isAutoEndTime() {
      return this.category.prefix === categoryConfig.autoEndTime.prefix;
    },
    isMultiGameDifficulty: function isMultiGameDifficulty() {
      return _isMultiGameDifficulty(this.category.prefix);
    },
    visibleCategory: function visibleCategory() {
      if (this.category.properties && typeof this.category.properties !== 'undefined' && typeof this.category.properties.visible !== 'undefined' && this.category.properties.visible === 2) {
        return false;
      }
      return true;
    },
    isPresetConfig: function isPresetConfig() {
      return this.category.resource_type === presetConfigCategoryResourceType;
    },
    isPresetCategory: function isPresetCategory() {
      return _isPresetCategory(this.category);
    },
    isPresetConfigParent: function isPresetConfigParent() {
      return isPresetConfigParentCategory(this.category);
    }
  }),

  methods: _extends({}, mapMutations({
    addValidator: 'creative/template/addValidator'
  }), mapActions({
    updateTargetCategory: 'creative/template/updateTargetCategory'
  }), {
    toggleSubCategory: function toggleSubCategory() {
      this.fold = !this.fold;
    }
  })
};